<template>
  <div>
    <template v-if="!spotAreas.length && !isLoading">
      <yard-view-no-areas />
    </template>
    <template v-else>
      <template v-if="viewMode === YardViewModeEnum.GRID">
        <v-expansion-panels
          class="d-flex flex-column gap-4"
          variant="accordion"
          multiple
          flat
          v-model="spotAreaPanels">
          <v-expansion-panel
            class="yard-spot-area-container"
            v-for="spotArea of spotAreas"
            :key="spotArea.id"
            :value="spotArea.id"
            :data-testid="`yard-main-area-${spotArea.id}`">
            <v-expansion-panel-title>
              <yard-view-spot-area-header
                :spot-area="spotArea"
                :data-testid="`yard-main-area-header-${spotArea.id}`" />
            </v-expansion-panel-title>

            <v-expansion-panel-text class="yard-spot-area-content">
              <yard-view-spot-area
                :editable="false"
                :spot-area="spotArea"
                class="mt-6"
                :data-testid="`yard-main-area-spots-${spotArea.id}`" />
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </template>
      <template v-if="viewMode === YardViewModeEnum.LIST">
        <yard-view-list-view />
      </template>
    </template>

    <yard-view-legend v-if="spotAreas.length > 0 && viewMode !== YardViewModeEnum.LIST" />
    <appointment-details-dialog
      v-if="appointment?.id"
      :external-activator="true"
      :show-dialog="Boolean(appointment)"
      @close="handleCloseAppointmentDialog"
      :appointment="appointment"></appointment-details-dialog>

    <yard-view-message-driver-dialog v-if="spotAreas.length > 0" />
  </div>
</template>

<script>
import { computed, defineComponent, onBeforeMount, onBeforeUnmount, ref } from 'vue';
import { get } from 'lodash';
import { useRouter } from 'vue-router';
import useYard from '@/modules/yard/composables/useYard';
import YardViewSpotArea from '@/modules/yard/components/SpotArea/YardViewSpotArea.vue';
import YardViewNoAreas from '@/modules/yard/components/Overlays/YardViewNoAreas.vue';
import YardViewSpotAreaHeader from '@/modules/yard/components/SpotArea/YardViewSpotAreaHeader.vue';
import { useEventHub, useSockets } from '@/composables';
import YardViewMessageDriverDialog from '@/modules/yard/components/Spot/Dialogs/YardViewMessageDriverDialog.vue';
import AppointmentDetailsDialog from '@/modules/appointments/components/details/AppointmentDetailsDialog.vue';
import { YardViewModeEnum } from '@nova/core';

export default defineComponent({
  name: 'YardViewYard',
  components: {
    YardViewMessageDriverDialog,
    YardViewSpotAreaHeader,
    YardViewNoAreas,
    YardViewSpotArea,
    AppointmentDetailsDialog
  },
  props: {},
  setup() {
    const yardStore = useYard();
    const router = useRouter();
    const sockets = useSockets();
    const eventHub = useEventHub();

    const messageDriverSpotAssignment = ref(null);
    const appointment = ref(null);
    const spotAreaPanels = ref([]);

    const selectedWarehouse = yardStore.selectedWarehouseRef;

    const spotAreas = computed(() =>
      yardStore.spotAreasRef.value.filter(spotArea =>
        yardStore.selectedAreasRef.value.includes(spotArea.id)
      )
    );

    const expandPanel = spotCode => {
      const spot = yardStore.spotsRef.value.find(s => s.code === spotCode);
      if (spot?.spotAreaId && spotAreas.value.find(sa => sa.id === spot.spotAreaId)) {
        if (!spotAreaPanels.value.includes(spot.spotAreaId)) {
          spotAreaPanels.value.push(spot.spotAreaId);
        }
      }
    };

    const handleAppointmentUpdated = event => {
      const eventAppointmentId = get(event, 'id');
      const appointmentId = get(appointment.value, 'id');

      if (appointmentId != null && eventAppointmentId === appointmentId) {
        const fields = ['status', 'refNumber', 'notes', 'ccEmails', 'loadTypeId', 'tags', 'userId'];
        fields.forEach(field => {
          if (event[field]) {
            appointment.value[field] = event[field];
          }
        });
      }
    };

    const handleCloseAppointmentDialog = () => {
      eventHub.$off('update-Appointment', handleAppointmentUpdated);

      appointment.value = null;
    };

    if (selectedWarehouse.value?.id) {
      router.push({ name: 'yard-warehouse', params: { warehouseId: selectedWarehouse.value.id } });
    }

    onBeforeMount(() => {
      Object.keys(sockets.actions).forEach(operation => {
        eventHub.$on(`${operation}-Appointment`, yardStore.handleAppointmentSubspaceEvent);
        eventHub.$on(`${operation}-SpotArea`, yardStore.handleSpotAreaSubspaceEvent);
        eventHub.$on(`${operation}-Spot`, yardStore.handleSpotSubspaceEvent);
        eventHub.$on(`${operation}-SpotAssignment`, yardStore.handleSpotAssignmentSubspaceEvent);
        eventHub.$on(yardStore.SPOT_ASSIGNMENT_CHANGED, ({ newSpotCode }) =>
          expandPanel(newSpotCode)
        );
        eventHub.$on(yardStore.GO_TO_SPOT, ({ newSpotCode }) => expandPanel(newSpotCode));
      });
      yardStore.selectedSpotRef.value = null;

      eventHub.$on('yard-view-appointment', ({ appointmentId, assetVisitId }) => {
        if (appointmentId) {
          yardStore.getAppointmentById(appointmentId).then(appt => {
            // Add the warehouse and the asset visit to the appointment
            const fullAppointment = {
              ...appt,
              dock: { ...appt.dock, warehouse: selectedWarehouse.value }
            };

            if (assetVisitId) {
              fullAppointment.assetVisit = yardStore.waitingAssetVisitsRef.value.find(
                assetVisit => assetVisit.id === assetVisitId
              );
            }

            appointment.value = fullAppointment;
            eventHub.$on('update-Appointment', handleAppointmentUpdated);
          });
        }
      });

      yardStore.trackMixpanelEvent(yardStore.MixPanelEvent.VIEW_PAGE, {
        metadata: {
          page: 'Live Yard View'
        }
      });
    });

    onBeforeUnmount(() => {
      Object.keys(sockets.actions).forEach(operation => {
        eventHub.$off(`${operation}-Appointment`, yardStore.handleAppointmentSubspaceEvent);
        eventHub.$off(`${operation}-SpotArea`, yardStore.handleSpotAreaSubspaceEvent);
        eventHub.$off(`${operation}-Spot`, yardStore.handleSpotSubspaceEvent);
        eventHub.$off(`${operation}-SpotAssignment`, yardStore.handleSpotAssignmentSubspaceEvent);
        eventHub.$off(yardStore.SPOT_ASSIGNMENT_CHANGED);
        eventHub.$off('yard-view-appointment');
      });

      yardStore.selectedSpotRef.value = null;
      yardStore.selectedAreasRef.value = [];
    });

    return {
      onBeforeMount,
      onBeforeUnmount,
      appointment,
      selectedWarehouse,
      spotAreas,
      spotAreaPanels,
      isLoading: yardStore.isLoadingSpotsRef,
      selectedSpot: yardStore.selectedSpotRef,
      messageDriverSpotAssignment,
      handleCloseAppointmentDialog,
      YardViewModeEnum,
      viewMode: yardStore.viewModeRef
    };
  }
});
</script>

<style lang="scss" scoped>
.yard-spot-area-container {
  border: 1px solid $color-line-border;
  border-radius: $default-border-radius;
}

.yard-spot-area-content {
  border-top: 1px solid $color-line-border;
}
</style>
